import { useState } from 'react'
import { ItemPickerDropdown } from './item-picker-dropdown'
import { resolveLaborRates } from '@/lib/labor-rates'
import { useLineItemRow } from '@/hooks/use-line-item-row'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
)

const HardHatIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5"/><path d="M14 6a6 6 0 0 1 6 6v3"/><path d="M4 15v-3a6 6 0 0 1 6-6"/><rect x="2" y="15" width="20" height="4" rx="1"/>
  </svg>
)

const fmt = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const selectAll = (e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()

/** Standard working hours per day — anything above is overtime */
const REGULAR_HOURS = 8

interface LaborSectionProps {
  lineItems: LineItem[]
  pricingItems: PricingItem[]
  section?: string
  serviceFilter?: string
  label?: string
  isPublicWorks?: boolean
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

/** Compute overtime hours: anything over 8 hrs/day × men × days */
function computeOvertimeHours(men: number, hrsPerDay: number, days: number): number {
  const otPerManPerDay = Math.max(0, hrsPerDay - REGULAR_HOURS)
  return otPerManPerDay * men * days
}

/** Compute line total with overtime split */
function computeLineTotal(men: number, hrsPerDay: number, days: number, rate: number, otRate: number): number {
  const regHrsPerDay = Math.min(hrsPerDay, REGULAR_HOURS)
  const otHrsPerDay = Math.max(0, hrsPerDay - REGULAR_HOURS)
  const costPerDay = men * regHrsPerDay * rate
  const otCostPerDay = men * otHrsPerDay * otRate
  return (costPerDay + otCostPerDay) * days
}

export function LaborSection({ lineItems, pricingItems, section, serviceFilter, label, isPublicWorks, onAddItem, onUpdateItem, onDeleteItem }: LaborSectionProps) {
  const laborItems = pricingItems.filter((p) => {
    if (p.category !== 'labor') return false
    if (!serviceFilter) return true
    if (!p.service) return false
    return p.service.split(',').map(s => s.trim()).includes(serviceFilter)
  })
  const rows = lineItems.filter((li) => li.category === 'labor' && li.section === (section || null))

  const handleSelect = async (item: PricingItem) => {
    const { rate: effectiveRate, overtimeRate: effectiveOvertimeRate } = resolveLaborRates(
      item,
      serviceFilter,
      isPublicWorks,
    )
    const men = 1
    const hrsPerDay = 8
    const days = 1
    const otHrs = computeOvertimeHours(men, hrsPerDay, days)
    await onAddItem({
      description: item.name,
      category: 'labor',
      quantity: men,
      unit: 'hr',
      rate: effectiveRate,
      pricingItemId: item.id,
      hoursPerDay: hrsPerDay,
      daysPerWeek: days,
      overtimeHours: otHrs,
      overtimeRate: effectiveOvertimeRate,
      section: section,
    })
  }

  const handleFieldChange = async (li: LineItem, updates: Record<string, number>) => {
    const merged = { ...li, ...updates }
    const men = merged.quantity || 0
    const hrsPerDay = merged.hoursPerDay || 0
    const days = merged.daysPerWeek || 0
    const rate = merged.rate
    const otRate = merged.overtimeRate || 0
    const otHrs = computeOvertimeHours(men, hrsPerDay, days)
    const lineTotal = computeLineTotal(men, hrsPerDay, days, rate, otRate)
    await onUpdateItem(li.id, { ...updates, overtimeHours: otHrs, lineTotal })
  }

  const laborTotal = rows.reduce((sum, li) => {
    const men = li.quantity || 0
    const hrsPerDay = li.hoursPerDay || 0
    const days = li.daysPerWeek || 0
    return sum + computeLineTotal(men, hrsPerDay, days, li.rate, li.overtimeRate || 0)
  }, 0)

  return (
    <div className="sc-section">
      <div className="sc-section-header">
        <HardHatIcon />
        <span className="sc-section-title">{label ?? 'Labor'}</span>
        <div className="sc-section-actions" onClick={(e) => e.stopPropagation()}>
          <ItemPickerDropdown items={laborItems} onSelect={handleSelect} isPublicWorks={isPublicWorks} serviceFilter={serviceFilter} />
        </div>
      </div>

      <div className="sc-table-wrap">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Labor</th>
              <th>Men</th>
              <th>Hrs/Day</th>
              <th>Days</th>
              <th>Rate/Hr</th>
              <th>Cost/Day</th>
              <th>OT Hrs</th>
              <th>OT Rate</th>
              <th>OT Cost/Day</th>
              <th className="col-right">Total</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((li) => (
              <LaborRow
                key={li.id}
                item={li}
                onFieldChange={handleFieldChange}
                onDelete={() => onDeleteItem(li.id)}
                onRename={(id, description) => onUpdateItem(id, { description })}
              />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={9} className="col-right"><strong>Total</strong></td>
                <td className="col-right"><span className="sc-foot-total">{fmt(laborTotal)}</span></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function LaborRow({
  item, onFieldChange, onDelete, onRename,
}: {
  item: LineItem
  onFieldChange: (li: LineItem, updates: Record<string, number>) => Promise<void>
  onDelete: () => void
  onRename: (itemId: string, description: string) => Promise<void>
}) {
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(item.description)

  const { values: lv, setField, onFocus, onBlur } = useLineItemRow(
    item,
    (it) => ({
      quantity: it.quantity,
      hoursPerDay: it.hoursPerDay || 8,
      daysPerWeek: it.daysPerWeek || 1,
      rate: it.rate,
    }),
    (v) => { onFieldChange(item, v) },
  )

  // Computed values
  const regHrsPerDay = Math.min(lv.hoursPerDay, REGULAR_HOURS)
  const otHrsPerDay = Math.max(0, lv.hoursPerDay - REGULAR_HOURS)
  const otRate = item.overtimeRate || 0
  const costPerDay = lv.quantity * regHrsPerDay * lv.rate
  const otCostPerDay = lv.quantity * otHrsPerDay * otRate
  const totalOtHrs = otHrsPerDay * lv.quantity * lv.daysPerWeek
  const total = (costPerDay + otCostPerDay) * lv.daysPerWeek

  return (
    <tr>
      <td className="col-name">
        {editingName ? (
          <input
            className="sc-input"
            style={{ minWidth: '120px' }}
            value={nameValue}
            autoFocus
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => { setEditingName(false); onRename(item.id, nameValue) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setEditingName(false); onRename(item.id, nameValue) } if (e.key === 'Escape') { setEditingName(false); setNameValue(item.description) } }}
          />
        ) : (
          <span className="sc-input-rename" title="Click to rename" onClick={() => setEditingName(true)}>{nameValue}</span>
        )}
      </td>
      <td><input type="number" className="sc-input" value={lv.quantity || ''}
        onChange={(e) => setField('quantity', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td><input type="number" className="sc-input" value={lv.hoursPerDay || ''}
        onChange={(e) => setField('hoursPerDay', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td><input type="number" className="sc-input" value={lv.daysPerWeek || ''}
        onChange={(e) => setField('daysPerWeek', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td>
        <span className="sc-input-readonly" title="Rate is set by admin in Pricing">{fmt(lv.rate).replace('$', '')}</span>
      </td>
      <td>{fmt(costPerDay)}</td>
      <td><span className="sc-input-readonly">{totalOtHrs > 0 ? totalOtHrs : 0}</span></td>
      <td><span className="sc-input-readonly">{otRate > 0 ? fmt(otRate).replace('$', '') : '—'}</span></td>
      <td>{otCostPerDay > 0 ? fmt(otCostPerDay) : '—'}</td>
      <td className="col-right"><strong>{fmt(total)}</strong></td>
      <td className="col-action">
        <button onClick={onDelete} className="sc-icon-btn" title="Delete"><DeleteIcon /></button>
      </td>
    </tr>
  )
}
